import { z } from 'zod'

import { SENDING_PURPOSE_SCHEMA } from '@/lib/domains/sending-purpose'
import { HTML_IMPORT_MAX_BYTES } from '@/lib/html-to-email/limits'

export const EMAIL_AUDIT_SCHEMA_VERSION = 1 as const
export const EMAIL_AUDIT_RULESET_VERSION = '2026-10-09.1'
export const EMAIL_AUDIT_MAX_FINDINGS = 100
export const EMAIL_AUDIT_PURPOSE_SCHEMA = z.enum([
  'marketing',
  'transactional',
  'unknown',
])
const EMAIL_AUDIT_POLICY_SOURCE_SCHEMA = z.enum([
  'provided',
  'defaulted',
  'trusted_adapter',
])
/** The report adds `inferred`: the semantic lane judged a defaulted purpose. */
const EMAIL_AUDIT_REPORT_POLICY_SOURCE_SCHEMA = z.enum([
  ...EMAIL_AUDIT_POLICY_SOURCE_SCHEMA.options,
  'inferred',
])

const EMAIL_AUDIT_CATEGORY_SCHEMA = z.enum([
  'compliance',
  'links',
  'images',
  'accessibility',
  'compatibility',
  'copy',
  'size',
  'markup',
])

const EMAIL_AUDIT_SEVERITY_SCHEMA = z.enum([
  'blocker',
  'error',
  'warning',
  'info',
])

const EMAIL_AUDIT_IMPACT_SCHEMA = z.enum(['block', 'confirm', 'advisory'])
const EMAIL_AUDIT_FINDING_ID_MAX_LENGTH = 512
const EMAIL_AUDIT_RULE_ID_MAX_LENGTH = 128
export const EMAIL_AUDIT_TEXT_MAX_LENGTH = 1000
export const EMAIL_AUDIT_TARGET_TEXT_MAX_LENGTH = 300
const EMAIL_AUDIT_SOURCE_MAX_LENGTH = 64
const EMAIL_AUDIT_MAX_SOURCES = 16
/** Transport cap on the subject and previewText request fields. */
export const EMAIL_AUDIT_COPY_FIELD_MAX_LENGTH = 1000
/**
 * The ONE JSON request budget for audit content: enforced on the public
 * operation body (MCP registry / HTTP binder) and mirrored by the session
 * action transport's decompressed-payload cap. Change it here, everywhere.
 */
export const EMAIL_AUDIT_MAX_REQUEST_BODY_BYTES = 6 * 1024 * 1024

const EMAIL_AUDIT_NOT_APPLICABLE_REASON_SCHEMA = z.enum([
  // The last four reasons only appear on reports stored before ruleset
  // 2026-10-02.1, which listed out-of-scope checks; reads must still parse.
  'no_remote_links',
  'no_remote_images',
  'no_remote_assets',
  'missing_copy',
  'requires_sending_domain',
  'send_transport_owned',
  'requires_audience_context',
  'separate_deliverability_test',
  'transactional_purpose',
  'unknown_purpose',
])

const EMAIL_AUDIT_TARGET_SCHEMA = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('email') }).strict(),
  z.object({ kind: z.literal('subject') }).strict(),
  z.object({ kind: z.literal('preview_text') }).strict(),
  z
    .object({
      kind: z.literal('link'),
      index: z.number().int().nonnegative(),
      displayUrl: z.string().max(EMAIL_AUDIT_TARGET_TEXT_MAX_LENGTH),
    })
    .strict(),
  z
    .object({
      kind: z.literal('image'),
      index: z.number().int().nonnegative(),
      displayUrl: z.string().max(EMAIL_AUDIT_TARGET_TEXT_MAX_LENGTH),
    })
    .strict(),
  z
    .object({
      kind: z.literal('element'),
      selector: z.string().min(1).max(EMAIL_AUDIT_TARGET_TEXT_MAX_LENGTH),
    })
    .strict(),
])

/**
 * Measured or judged support for a finding, so a reader sees how bad it is:
 * the HTTP answer a destination gave, the email clients that drop a feature,
 * or the probability the evaluation model put on a semantic judgment.
 */
const EMAIL_AUDIT_EVIDENCE_SCHEMA = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('http'),
      status: z.number().int().min(100).max(599).nullable(),
      failure: z
        .enum(['dns', 'tls', 'connect', 'timeout', 'private_host', 'redirects'])
        .optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('clients'),
      unsupported: z.array(z.string().min(1).max(64)).min(1).max(24),
    })
    .strict(),
  z
    .object({
      kind: z.literal('judgment'),
      question: z.string().min(1).max(64),
      probability: z.number().min(0).max(1),
    })
    .strict(),
])

export const EMAIL_AUDIT_FINDING_SCHEMA = z
  .object({
    id: z.string().min(1).max(EMAIL_AUDIT_FINDING_ID_MAX_LENGTH),
    ruleId: z.string().min(1).max(EMAIL_AUDIT_RULE_ID_MAX_LENGTH),
    category: EMAIL_AUDIT_CATEGORY_SCHEMA,
    severity: EMAIL_AUDIT_SEVERITY_SCHEMA,
    impact: EMAIL_AUDIT_IMPACT_SCHEMA,
    message: z.string().min(1).max(EMAIL_AUDIT_TEXT_MAX_LENGTH),
    remediation: z.string().min(1).max(EMAIL_AUDIT_TEXT_MAX_LENGTH),
    occurrenceCount: z
      .number()
      .int()
      .positive()
      .optional()
      .describe(
        'Total occurrences of this RULE across the email, stamped on every representative finding of the rule so the total survives representative truncation. Reconcile by max per rule — summing across findings overcounts.'
      ),
    sources: z
      .array(z.string().min(1).max(EMAIL_AUDIT_SOURCE_MAX_LENGTH))
      .min(1)
      .max(EMAIL_AUDIT_MAX_SOURCES),
    standards: z
      .array(
        z
          .object({
            id: z.string().min(1).max(128),
            url: z
              .string()
              .max(2048)
              .url()
              .regex(/^https?:\/\//)
              .optional(),
          })
          .strict()
      )
      .max(8)
      .optional(),
    evidence: EMAIL_AUDIT_EVIDENCE_SCHEMA.optional(),
    target: EMAIL_AUDIT_TARGET_SCHEMA,
  })
  .strict()

const EMAIL_AUDIT_CHECK_SCHEMA = z.discriminatedUnion('status', [
  z
    .object({
      id: z.string().min(1).max(128),
      status: z.literal('passed'),
      durationMs: z.number().int().nonnegative(),
      findingCount: z.literal(0),
    })
    .strict(),
  z
    .object({
      id: z.string().min(1).max(128),
      status: z.literal('issues'),
      durationMs: z.number().int().nonnegative(),
      findingCount: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      id: z.string().min(1).max(128),
      status: z.literal('not_applicable'),
      reason: EMAIL_AUDIT_NOT_APPLICABLE_REASON_SCHEMA,
    })
    .strict(),
  z
    .object({
      id: z.string().min(1).max(128),
      status: z.literal('unavailable'),
      reason: z.enum(['timeout', 'upstream', 'invalid_response']),
      retryable: z.boolean(),
      durationMs: z.number().int().nonnegative(),
    })
    .strict(),
])

const EMAIL_AUDIT_LOADED_SIZE_SCHEMA = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('exact'),
      htmlBytes: z.number().int().nonnegative(),
      remoteAssetBytes: z.number().int().nonnegative(),
      totalBytes: z.number().int().nonnegative(),
      assetCount: z.number().int().nonnegative(),
    })
    .strict(),
  z
    .object({
      status: z.literal('lower_bound'),
      htmlBytes: z.number().int().nonnegative(),
      knownRemoteAssetBytes: z.number().int().nonnegative(),
      knownTotalBytes: z.number().int().nonnegative(),
      unknownAssetCount: z.number().int().positive(),
    })
    .strict(),
])

const EMAIL_AUDIT_COMMON_RESPONSE_SCHEMA = z
  .object({
    schemaVersion: z.literal(EMAIL_AUDIT_SCHEMA_VERSION),
    rulesetVersion: z.string().min(1).max(100),
    auditId: z.string().uuid(),
    contentHash: z.string().regex(/^sha256:[0-9a-f]{64}$/),
    auditedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    policy: z
      .object({
        purpose: EMAIL_AUDIT_PURPOSE_SCHEMA,
        source: EMAIL_AUDIT_REPORT_POLICY_SOURCE_SCHEMA,
        /** Model confidence when `source` is `inferred`. */
        confidence: z.number().min(0).max(1).optional(),
        unsubscribe: z.enum(['required', 'not_required', 'not_evaluated']),
      })
      .strict(),
    summary: z
      .object({
        blockers: z.number().int().nonnegative(),
        errors: z.number().int().nonnegative(),
        warnings: z.number().int().nonnegative(),
        info: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
      })
      .strict(),
    checks: z.array(EMAIL_AUDIT_CHECK_SCHEMA).max(32),
    metrics: z
      .object({
        htmlBytes: z.number().int().nonnegative(),
        linkCount: z.number().int().nonnegative(),
        imageCount: z.number().int().nonnegative(),
        gifCount: z.number().int().nonnegative(),
        loadedSize: EMAIL_AUDIT_LOADED_SIZE_SCHEMA,
      })
      .strict(),
    findings: z.array(EMAIL_AUDIT_FINDING_SCHEMA).max(EMAIL_AUDIT_MAX_FINDINGS),
    totalFindings: z.number().int().nonnegative(),
    findingsTruncated: z.boolean(),
  })
  .strict()

const EMAIL_AUDIT_COMPLETION_SCHEMA = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('complete'),
      readiness: z.enum(['ready', 'needs_review', 'not_ready']),
      score: z.number().int().min(0).max(100),
    })
    .strict(),
  z
    .object({
      status: z.literal('partial'),
      readiness: z.enum(['unknown', 'not_ready']),
      score: z.null(),
    })
    .strict(),
])

export const EMAIL_AUDIT_RESPONSE_SCHEMA =
  EMAIL_AUDIT_COMMON_RESPONSE_SCHEMA.extend({
    completion: EMAIL_AUDIT_COMPLETION_SCHEMA,
  }).strict()

function utf8Length(value: string): number {
  return new TextEncoder().encode(value).byteLength
}

function boundedContent(field: string) {
  return z
    .string()
    .min(1)
    .refine((value) => value.trim().length > 0, {
      message: `${field} must contain non-whitespace content`,
    })
    .refine((value) => utf8Length(value) <= HTML_IMPORT_MAX_BYTES, {
      message: `${field} must not exceed ${HTML_IMPORT_MAX_BYTES} UTF-8 bytes`,
    })
}

const EMAIL_AUDIT_ENVELOPE_FIELDS = {
  subject: z
    .string()
    .max(EMAIL_AUDIT_COPY_FIELD_MAX_LENGTH)
    .optional()
    .describe(
      'Subject line to judge. Omit it and the subject is not evaluated; an empty string is reported as a missing subject.'
    ),
  previewText: z
    .string()
    .max(EMAIL_AUDIT_COPY_FIELD_MAX_LENGTH)
    .optional()
    .describe(
      'Inbox preview text. Omit it to read the preheader from the email.'
    ),
  sendingPurpose: SENDING_PURPOSE_SCHEMA.optional().describe(
    'marketing (unsubscribe link and postal address required) or transactional. Omit it and the audit infers the purpose from the content.'
  ),
}

/** Audit content as HTML: what every audit lane reads. */
export const EMAIL_AUDIT_REQUEST_SCHEMA = z
  .object({
    emailHtml: boundedContent('emailHtml').describe('Rendered email HTML.'),
    ...EMAIL_AUDIT_ENVELOPE_FIELDS,
  })
  .strict()

const EMAIL_AUDIT_JSX_REQUEST_SCHEMA = z
  .object({
    emailJsx: boundedContent('emailJsx').describe(
      'React Email JSX, rendered by Brew before the audit.'
    ),
    ...EMAIL_AUDIT_ENVELOPE_FIELDS,
  })
  .strict()

const EMAIL_AUDIT_SAVED_DESIGN_REQUEST_SCHEMA = z
  .object({
    emailId: z
      .string()
      .trim()
      .min(1)
      .max(64)
      .describe('A saved email design, rendered exactly as a send renders it.'),
    emailVersionId: z
      .string()
      .trim()
      .min(1)
      .max(80)
      .optional()
      .describe('The exact design version; omit for the latest version.'),
    ...EMAIL_AUDIT_ENVELOPE_FIELDS,
  })
  .strict()

/**
 * The public request as its three variants: rendered HTML, React Email JSX,
 * or a saved design. The published contract (OpenAPI `oneOf`, so SDK types
 * are a real union) is generated from this.
 */
export const EMAIL_AUDIT_SOURCE_VARIANTS_SCHEMA = z.union([
  EMAIL_AUDIT_REQUEST_SCHEMA,
  EMAIL_AUDIT_JSX_REQUEST_SCHEMA,
  EMAIL_AUDIT_SAVED_DESIGN_REQUEST_SCHEMA,
])

const EMAIL_AUDIT_CONTENT_SOURCES = [
  'emailHtml',
  'emailJsx',
  'emailId',
] as const

/**
 * The same request flattened into one object for runtime parsing (HTTP
 * binder, MCP tool input — connector JSON-Schema dialects reject unions):
 * every field comes from the variants above, and a refinement enforces
 * exactly one content source.
 */
export const EMAIL_AUDIT_SOURCE_REQUEST_SCHEMA = z
  .object({
    ...EMAIL_AUDIT_ENVELOPE_FIELDS,
    emailHtml: EMAIL_AUDIT_REQUEST_SCHEMA.shape.emailHtml.optional(),
    emailJsx: EMAIL_AUDIT_JSX_REQUEST_SCHEMA.shape.emailJsx.optional(),
    emailId: EMAIL_AUDIT_SAVED_DESIGN_REQUEST_SCHEMA.shape.emailId.optional(),
    emailVersionId:
      EMAIL_AUDIT_SAVED_DESIGN_REQUEST_SCHEMA.shape.emailVersionId,
  })
  .strict()
  .superRefine((value, context) => {
    const sources = EMAIL_AUDIT_CONTENT_SOURCES.filter(
      (key) => value[key] !== undefined
    )
    if (sources.length !== 1) {
      context.addIssue({
        code: 'custom',
        message: 'Send exactly one of emailHtml, emailJsx or emailId.',
        path: sources.length > 1 ? [sources[1] ?? 'emailHtml'] : ['emailHtml'],
      })
    }
    if (value.emailVersionId !== undefined && value.emailId === undefined) {
      context.addIssue({
        code: 'custom',
        message: 'emailVersionId requires emailId.',
        path: ['emailVersionId'],
      })
    }
  })

export const EMAIL_AUDIT_INPUT_SCHEMA = EMAIL_AUDIT_REQUEST_SCHEMA.omit({
  sendingPurpose: true,
})
  .extend({
    policy: z
      .object({
        purpose: EMAIL_AUDIT_PURPOSE_SCHEMA,
        source: EMAIL_AUDIT_POLICY_SOURCE_SCHEMA,
      })
      .strict(),
  })
  .strict()

/**
 * Everything the audit executor accepts: the public source request, or the
 * internal input a trusted door resolves (a stored email's HTML with its
 * `policy`). HTTP parses only `EMAIL_AUDIT_SOURCE_REQUEST_SCHEMA`, so a
 * public caller can never supply `policy` itself.
 */
export const EMAIL_AUDIT_EXECUTOR_INPUT_SCHEMA = z.union([
  EMAIL_AUDIT_SOURCE_REQUEST_SCHEMA,
  EMAIL_AUDIT_INPUT_SCHEMA,
])

export type EmailAuditRequest = z.input<typeof EMAIL_AUDIT_REQUEST_SCHEMA>
export type EmailAuditSourceRequest = z.input<
  typeof EMAIL_AUDIT_SOURCE_REQUEST_SCHEMA
>
export type EmailAuditInput = z.input<typeof EMAIL_AUDIT_INPUT_SCHEMA>
export type EmailAuditFinding = z.infer<typeof EMAIL_AUDIT_FINDING_SCHEMA>
export type EmailAuditEvidence = z.infer<typeof EMAIL_AUDIT_EVIDENCE_SCHEMA>
export type EmailAuditCheck = z.infer<typeof EMAIL_AUDIT_CHECK_SCHEMA>
export type EmailAuditNotApplicableReason = z.infer<
  typeof EMAIL_AUDIT_NOT_APPLICABLE_REASON_SCHEMA
>
export type EmailAuditResponse = z.infer<typeof EMAIL_AUDIT_RESPONSE_SCHEMA>
export type EmailAuditMetrics = EmailAuditResponse['metrics']
export type EmailAuditSummary = EmailAuditResponse['summary']
export type EmailAuditTarget = EmailAuditFinding['target']
